function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
/* eslint-disable react/no-unused-prop-types */
import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { Group } from '@visx/group';
import { scaleLinear, scaleOrdinal } from '@visx/scale';
import { Pie } from '@visx/shape';
import { Text as SVGText } from '@visx/text';
import { ParentSize } from '@visx/responsive';
import { Config } from '@leverege/plugin';
import TinyColor from 'tinycolor2';
import { ns } from '@leverege/i18n';
import { Content, Pane } from '@leverege/ui-elements';
import Coordinate from "../Coordinate.js";
import { PieChartModel } from "../models/index.js";
import { getDefaultIconForSeriesType, seriesDataIsEmpty, buildCustomPieChartTheme } from "./Util.js";
import S from './PieChartRenderer.css';
import { usePieData } from "../hooks/index.js";
import PieRolloverCard from "./PieRolloverCard.js";
import KeyRenderer from "./KeyRenderer.js";
import { DEFAULT_RANGES } from "../Constants.js";
import DonutTextRenderer from "./DonutTextRenderer.js";
const {
  t
} = ns('ui-attributes-graphs.viewers.PieChartRenderer');
const DEFAULT_MARGIN = {
  top: 10,
  right: 0,
  bottom: 10,
  left: 0
};
const DEFAULT_LABEL = {
  color: 'white',
  visible: true,
  threshold: 0.1,
  align: false
};
const DEFAULT_ROLLOVER = {
  visible: false,
  labelFlexVariant: null,
  labelTextVariant: 'detailValue',
  countFlexVariant: 'rowSpaceBetween',
  countTextVariant: 'detailLabel',
  countValueVariant: 'detailValue',
  percentFlexVariant: 'rowSpaceBetween',
  percentTextVariant: 'detailLabel',
  percentValueVariant: 'detailValue',
  labelVisible: true,
  countVisible: true,
  percentVisible: true
};
const DEFAULT_INNER_COUNT = {
  visible: true,
  color: 'var(--color-base700)',
  fontFamily: 'Inter',
  fontSize: 50,
  yOffset: 0
};
const DEFAULT_INNER_TITLE = {
  visible: true,
  color: 'var(--color-base600)',
  fontFamily: 'Inter',
  fontSize: 25,
  yOffset: 40
};
class PieChartRenderer extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      active: null
    };
    this.theme = buildCustomPieChartTheme();
    this.backgroundRef = /*#__PURE__*/React.createRef();
    const {
      objectType
    } = props;
    this.isAgg = objectType === 'aggResult';
  }
  getMargin() {
    const {
      model
    } = this.props;
    const margin = PieChartModel.getMargin(model);
    return Object.keys(DEFAULT_MARGIN).reduce((prev, curr) => {
      if (margin[curr] == null) {
        prev[curr] = DEFAULT_MARGIN[curr];
      } else {
        prev[curr] = margin[curr];
      }
      return prev;
    }, {});
  }
  getLabel() {
    const {
      model
    } = this.props;
    const label = PieChartModel.getLabel(model) || {};
    return Object.keys(DEFAULT_LABEL).reduce((prev, curr) => {
      if (label[curr] == null) {
        prev[curr] = DEFAULT_LABEL[curr];
      } else {
        prev[curr] = label[curr];
      }
      return prev;
    }, {});
  }
  getRollover() {
    const {
      model
    } = this.props;
    const rollover = PieChartModel.getRollover(model);
    return Object.keys(DEFAULT_ROLLOVER).reduce((prev, curr) => {
      if (rollover[curr] == null) {
        prev[curr] = DEFAULT_ROLLOVER[curr];
      } else {
        prev[curr] = rollover[curr];
      }
      return prev;
    }, {});
  }
  getInnerCount() {
    const {
      model
    } = this.props;
    const innerCount = PieChartModel.getInnerCount(model);
    return Object.keys(DEFAULT_INNER_COUNT).reduce((prev, curr) => {
      if (innerCount[curr] == null) {
        prev[curr] = DEFAULT_ROLLOVER[curr];
      } else {
        prev[curr] = innerCount[curr];
      }
      return prev;
    }, {});
  }
  getInnerTitle() {
    const {
      model
    } = this.props;
    const innerTitle = PieChartModel.getInnerTitle(model);
    return Object.keys(DEFAULT_INNER_TITLE).reduce((prev, curr) => {
      if (innerTitle[curr] == null) {
        prev[curr] = DEFAULT_ROLLOVER[curr];
      } else {
        prev[curr] = innerTitle[curr];
      }
      return prev;
    }, {});
  }
  getDefaultAttribute(objectType) {
    const attributes = Coordinate.getCoordinatesFor(objectType);
    return attributes[0];
  }
  getModelOptions() {
    const {
      model,
      objectType
    } = this.props;
    const attribute = PieChartModel.getAttribute(model) || this.getDefaultAttribute(objectType);
    const limit = PieChartModel.getLimit(model) || 10;
    const includeNull = PieChartModel.getIncludeNull(model) || false;
    const valueRanges = PieChartModel.getValueRanges(model) || DEFAULT_RANGES;
    const margin = PieChartModel.getMargin(model) || this.getMargin();
    const size = PieChartModel.getSize(model) || 115;
    const donutSize = PieChartModel.getDonutSize(model) || 0;
    const cornerRadius = PieChartModel.getCornerRadius(model) || null;
    const padAngle = PieChartModel.getPadAngle(model) || null;
    const strokeColor = PieChartModel.getStrokeColor(model) || null;
    const strokeWidth = PieChartModel.getStrokeWidth(model) || 1;
    const label = PieChartModel.getLabel(model) || this.getLabel();
    const backgroundColor = PieChartModel.getBackgroundColor(model) || this.theme.backgroundColor;
    const rollover = this.getRollover();
    const key = PieChartModel.getKey(model);
    const innerCount = PieChartModel.getInnerCount(model) || this.getInnerCount();
    const innerTitle = PieChartModel.getInnerTitle(model) || this.getInnerTitle();
    return {
      attribute,
      limit,
      includeNull,
      valueRanges,
      margin,
      size,
      donutSize,
      cornerRadius,
      padAngle,
      strokeColor,
      strokeWidth,
      label,
      rollover,
      backgroundColor,
      key,
      innerCount,
      innerTitle
    };
  }
  render() {
    const {
      data,
      noDataMessage,
      loading,
      width,
      height,
      objectType,
      query,
      sourceObjectType
    } = this.props;
    const {
      active
    } = this.state;
    const {
      attribute,
      limit,
      includeNull,
      valueRanges,
      margin,
      size,
      donutSize,
      cornerRadius,
      padAngle,
      strokeColor,
      strokeWidth,
      label,
      rollover,
      backgroundColor,
      key,
      innerCount,
      innerTitle
    } = this.getModelOptions();
    const dataIsEmpty = seriesDataIsEmpty(data);
    if (loading && dataIsEmpty) {
      return /*#__PURE__*/React.createElement(Content, {
        variant: "xyChartLoadingContent"
      }, /*#__PURE__*/React.createElement(Content.Area, {
        layout: "flex:center"
      }, t('loading')));
    }
    if (dataIsEmpty || objectType !== 'aggResult' && !attribute) {
      return /*#__PURE__*/React.createElement(Content, {
        variant: "xyChartNoDataContent"
      }, /*#__PURE__*/React.createElement(Content.Area, {
        layout: "flex:center"
      }, /*#__PURE__*/React.createElement(NoDataView, {
        message: noDataMessage,
        seriesType: "pie"
      })));
    }
    const sizeScale = scaleLinear({
      domain: [0, 150],
      range: [10, 0.01],
      clamp: true
    });
    const sizeScaleKey = scaleLinear({
      domain: [0, 150],
      range: [13, 0.01],
      clamp: true
    });
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;
    const centerY = innerHeight / 2;
    const centerX = innerWidth / 2;
    const outerRadius = Math.min(innerWidth, innerHeight) / (key?.visible ? sizeScaleKey(size) : sizeScale(size));
    const moveKey = innerWidth < outerRadius * 3.25;
    const pieLeft = key?.visible && !moveKey ? centerX + margin.left - outerRadius / 2 : centerX + margin.left;
    const groupLeft = pieLeft >= outerRadius + margin.left ? pieLeft : outerRadius + margin.left;
    const groupTop = key?.visible && moveKey ? margin.top + outerRadius : centerY + margin.top;
    const hideTitle = donutSize > 0 && innerTitle?.visible;
    return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("svg", {
      width: width,
      height: height,
      onMouseOver: evt => {
        if (evt.target === this.backgroundRef.current) {
          this.setState({
            active: null
          });
        }
      }
    }, /*#__PURE__*/React.createElement("rect", {
      ref: this.backgroundRef,
      width: width,
      height: height,
      fill: backgroundColor
    }), /*#__PURE__*/React.createElement(Group, {
      top: groupTop,
      left: groupLeft
    }, /*#__PURE__*/React.createElement(PieRenderer, {
      data: data,
      isAgg: this.isAgg,
      attribute: attribute,
      limit: limit,
      includeNull: includeNull,
      valueRanges: valueRanges,
      width: innerWidth,
      height: innerHeight,
      sizeScale: sizeScale,
      pieConfig: {
        outerRadius,
        donutSize,
        cornerRadius,
        padAngle,
        strokeColor,
        strokeWidth,
        label,
        theme: this.theme
      },
      setActive: active => this.setState({
        active
      }),
      active: active
    }), donutSize > 0 && /*#__PURE__*/React.createElement(DonutTextRenderer, {
      data: data,
      limit: limit,
      includeNull: includeNull,
      valueRanges: valueRanges,
      isAgg: this.isAgg,
      query: query,
      sourceObjectType: sourceObjectType,
      attribute: attribute,
      innerCount: innerCount,
      innerTitle: innerTitle,
      outerRadius: outerRadius
    }))), key?.visible && /*#__PURE__*/React.createElement(KeyRenderer, {
      data: data,
      isAgg: this.isAgg,
      query: query,
      sourceObjectType: sourceObjectType,
      limit: limit,
      includeNull: includeNull,
      valueRanges: valueRanges,
      attribute: attribute,
      theme: this.theme,
      outerRadius: outerRadius,
      width: width,
      height: height,
      pieLeft: groupLeft,
      margin: margin,
      keyConfig: key,
      moveKey: moveKey,
      active: active,
      hideTitle: hideTitle
    }), rollover.visible && active && /*#__PURE__*/React.createElement(PieRolloverCard, {
      active: active,
      top: groupTop,
      left: groupLeft,
      rollover: rollover,
      key: key?.visible
    }));
  }
}
PieChartRenderer.propTypes = {
  data: PropTypes.array,
  model: PropTypes.object.isRequired,
  objectType: PropTypes.string.isRequired,
  valueRanges: PropTypes.array,
  noDataMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  loading: PropTypes.bool
};
PieChartRenderer.defaultProps = {
  context: {},
  get noDataMessage() {
    return t('noDataMessage');
  },
  loading: false
};
function NoDataView({
  message
}) {
  const type = 'pie';
  let noDataIcon = Config.get('PieChart', `noDataIcon${type ? `.${type}` : ''}`, getDefaultIconForSeriesType(type));
  if (noDataIcon != null && typeof noDataIcon !== 'string') {
    // assume its a component class/function
    const Clazz = noDataIcon;
    noDataIcon = /*#__PURE__*/React.createElement(Clazz, null);
  } else {
    const noDataDimensions = Config.get('XYChart', 'noDataIconDimensions', {
      height: 224,
      width: 224
    });
    noDataIcon = /*#__PURE__*/React.createElement("img", {
      src: noDataIcon,
      alt: t('altNoData'),
      height: noDataDimensions.height,
      width: noDataDimensions.width
    });
  }
  return /*#__PURE__*/React.createElement(Pane, {
    variant: "noHistoryView|noDataView",
    className: classnames(S.win, S.noDataContainer),
    layout: "flex:rowL"
  }, noDataIcon, message);
}
function PieRenderer({
  data: dataProps,
  isAgg,
  attribute,
  limit,
  includeNull,
  valueRanges,
  pieConfig,
  sizeScale,
  active,
  setActive,
  width,
  height
}) {
  const data = usePieData(dataProps, {
    isAgg,
    attribute,
    valueRanges,
    limit,
    includeNull,
    pie: true
  });
  const {
    outerRadius,
    donutSize,
    cornerRadius,
    padAngle: pPadAngle,
    strokeColor,
    label,
    theme,
    strokeWidth
  } = pieConfig;
  const {
    color: labelColor,
    angle: labelAngle,
    fontFamily,
    fontSize,
    xOffset,
    yOffset,
    visible: labelVisible,
    threshold: labelThreshold,
    align
  } = label;
  if (Array.isArray(data)) {
    const getColor = scaleOrdinal({
      domain: data.map((l, i) => i),
      range: [...theme.colors]
    });

    // Scales padAngle value from more user friendly numbers
    const padAngleScale = scaleLinear({
      domain: [0, 10],
      range: [0.001, 0.1]
    });
    const scaledDonutSize = donutSize !== 0 ? Math.min(width, height) / sizeScale(donutSize) : 0;
    const innerRadius = scaledDonutSize < outerRadius ? scaledDonutSize : outerRadius - 1;
    const padAngle = padAngleScale(pPadAngle);
    const soloValue = data.filter(datum => datum.value > 0).length === 1;
    return /*#__PURE__*/React.createElement(Pie, {
      data: data,
      outerRadius: ({
        data
      }) => {
        if (active && active.name === data.name) return outerRadius * 1.015;
        return outerRadius;
      },
      innerRadius: innerRadius,
      cornerRadius: cornerRadius,
      padAngle: padAngle,
      pieValue: d => d.value
    }, ({
      arcs,
      path
    }) => {
      return arcs.map((arc, i) => {
        const [x, y] = path.centroid(arc);
        const hasSpaceForLabel = arc.endAngle - arc.startAngle >= labelThreshold;
        const color = getColor(i);
        let alignedAngle, alignedXOffset, alignedYOffset;
        if (align) {
          const alignValues = alignAngle(arc.startAngle, arc.endAngle, innerRadius > 0);
          alignedAngle = alignValues.alignedAngle;
          alignedXOffset = alignValues.alignedXOffset;
          alignedYOffset = alignValues.alignedYOffset;
        }
        const scaledIndex = scaleOrdinal({
          domain: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19],
          range: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
        });
        const computedColor = getComputedStyle(document.documentElement).getPropertyValue(`--color-graphSeries${scaledIndex(i)}`);
        const hover = active && active?.name === arc.data.name;
        const darkenValue = strokeColor ? 5 : 10;
        return /*#__PURE__*/React.createElement("g", {
          key: `pie-arc-${arc.data.name}`,
          onMouseOver: () => {
            setActive({
              ...arc.data,
              color,
              x,
              y
            });
          }
        }, /*#__PURE__*/React.createElement("path", {
          d: path(arc),
          fill: hover ? TinyColor(computedColor).darken(darkenValue) : color,
          stroke: strokeColor,
          strokeWidth: strokeWidth
        }), labelVisible && hasSpaceForLabel && /*#__PURE__*/React.createElement(SVGText, {
          x: x,
          y: y,
          verticalAnchor: "start",
          textAnchor: "middle",
          dy: soloValue ? -(outerRadius / 2) : align ? alignedYOffset : yOffset,
          dx: align ? alignedXOffset : xOffset,
          fontFamily: fontFamily,
          fontSize: fontSize,
          fill: labelColor,
          angle: align ? alignedAngle : labelAngle
        }, arc.data.name));
      });
    });
  }
  return null;
}

// Function to set label angle to align to center angle of its slice
function alignAngle(startAngle, endAngle, donut) {
  const centerAngle = (endAngle + startAngle) / 2;
  const labelAngle = centerAngle * 360 / (Math.PI * 2);
  let alignedAngle;
  if (donut) {
    alignedAngle = centerAngle < Math.PI / 1.75 || centerAngle > Math.PI * 1.5 ? labelAngle : labelAngle + 180;
  } else {
    alignedAngle = centerAngle < Math.PI ? labelAngle - 90 : labelAngle + 90;
  }
  const scaleXOffset = scaleLinear({
    domain: [-75, -45, -15, 15, 45, 75, 285, 315, 344, 375, 405, 435],
    range: [-7.5, -2.5, 0, 0, 2.5, 5, -5, -2.5, 0, 0, 2.5, 7]
  });
  const scaleYOffset = scaleLinear({
    domain: [-75, -45, -15, 15, 45, 75, 285, 315, 344, 375, 405, 435],
    range: [2.5, -2.5, -5, -5, -2.5, 0, 0, -2.5, -5, -5, -5, 0]
  });
  const alignedXOffset = scaleXOffset(alignedAngle);
  const alignedYOffset = scaleYOffset(alignedAngle);
  return {
    alignedAngle,
    alignedXOffset,
    alignedYOffset
  };
}
function WrappedChart(props) {
  return /*#__PURE__*/React.createElement(ParentSize, {
    parentSizeStyles: {
      overflow: 'hidden',
      width: '100%',
      height: '100%'
    }
  }, ({
    width,
    height
  }) => /*#__PURE__*/React.createElement(PieChartRenderer, _extends({}, props, {
    width: width,
    height: height
  })));
}
WrappedChart.propTypes = PieChartRenderer.propTypes;
WrappedChart.defaultProps = PieChartRenderer.defaultProps;
export default WrappedChart;